"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  api,
  loadTokens,
  type SubscriptionRecord,
} from "@/lib/api";
import { useDisplayCurrency } from "@/lib/useDisplayCurrency";
import { formatMoney } from "@/lib/format";
import { FirstSubscribeWalkthrough } from "@/components/posts/FirstSubscribeWalkthrough";

/**
 * EPIC-022 STORY-628 — subscribe / cancel button on `/u/[username]`.
 *
 * Resolves the viewer's current subscription state to this creator
 * on mount (so we can render the right CTA: subscribe / "Already
 * subscribed" / "Cancelled — access until X / re-subscribe"). Submit
 * confirms via window.confirm (subscribe = real money cost; cancel =
 * irreversible-this-period).
 *
 * Anonymous viewers see no button — sign-in is required to subscribe.
 */
export function SubscribeButton({
  creatorUsername,
  monthlyPriceCents,
  isOwnProfile,
}: {
  creatorUsername: string;
  monthlyPriceCents: string;
  isOwnProfile: boolean;
}) {
  const { t } = useTranslation();
  const displayCurrency = useDisplayCurrency();
  const [authed, setAuthed] = useState(false);
  const [sub, setSub] = useState<SubscriptionRecord | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!loadTokens()) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAuthed(true);
    let cancelled = false;
    void api
      .listMySubscriptions()
      .then((rows) => {
        if (cancelled) return;
        const match = rows.find(
          (r) => r.creatorUsername.toLowerCase() === creatorUsername.toLowerCase(),
        );
        setSub(match ?? null);
      })
      .catch(() => {
        // Anonymous-style fallback — show generic Subscribe button.
      });
    return () => {
      cancelled = true;
    };
  }, [creatorUsername]);

  if (isOwnProfile || !authed) return null;

  const price = formatMoney(monthlyPriceCents, displayCurrency);

  async function handleSubscribe() {
    if (
      !window.confirm(
        t("subscribeButton.confirmSubscribeTemplate", {
          username: creatorUsername,
          price,
        }),
      )
    ) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.subscribeToCreator(creatorUsername);
      setSub(res.subscription);
      window.dispatchEvent(new CustomEvent("granbay.balance.changed"));
      // 2026-05-04 — fire `granbay.subscribe.success` on EVERY
      // successful subscribe (renamed from `subscribe.first` per
      // founder direction "force the pop for the subscriber
      // everytime, give the user to tick to not see the pop again").
      // The walkthrough modal renders an opt-out checkbox that, when
      // ticked + closed, writes the SEEN_KEY localStorage flag to
      // suppress future pops. Without the tick, every subscribe
      // re-opens the modal.
      window.dispatchEvent(
        new CustomEvent("granbay.subscribe.success", {
          detail: { username: creatorUsername },
        }),
      );
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("subscribeButton.subscribeFailed"),
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCancel() {
    if (!sub) return;
    if (
      !window.confirm(
        t("subscribeButton.confirmCancelTemplate", {
          username: creatorUsername,
          when: new Date(sub.currentPeriodExpiresAt).toLocaleDateString(),
        }),
      )
    ) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const next = await api.cancelSubscription(sub.id);
      setSub(next);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("subscribeButton.cancelFailed"),
      );
    } finally {
      setSubmitting(false);
    }
  }

  // Static comparison vs server-issued timestamp; clock-drift is fine
  // at closed-beta scale. The button refreshes on next mount.
  const stillHasAccess =
    sub !== null &&
    (sub.state === "active" ||
      sub.state === "cancelled" ||
      sub.state === "failed_renewal_grace") &&
    // eslint-disable-next-line react-hooks/purity
    new Date(sub.currentPeriodExpiresAt).getTime() > Date.now();

  return (
    <div className="flex flex-col items-end gap-1">
      <FirstSubscribeWalkthrough creatorUsername={creatorUsername} />
      {sub && sub.state === "active" && (
        <button
          type="button"
          onClick={handleCancel}
          disabled={submitting}
          className="min-h-[40px] rounded-md border px-3 py-1.5 text-[11px] font-bold transition-colors disabled:opacity-50 md:min-h-0"
          style={{
            borderColor: "var(--color-text3)",
            color: "var(--color-text3)",
          }}
        >
          {submitting ? "…" : t("subscribeButton.cancelSubscription")}
        </button>
      )}
      {(!sub || sub.state === "cancelled" || sub.state === "expired") && (
        <button
          type="button"
          onClick={handleSubscribe}
          disabled={submitting}
          className="min-h-[40px] rounded-md px-3 py-1.5 text-[11px] font-bold text-white transition-colors disabled:opacity-50 md:min-h-0"
          style={{ background: "var(--color-brand)" }}
        >
          {submitting
            ? "…"
            : sub && sub.state === "cancelled" && stillHasAccess
              ? t("subscribeButton.renewTemplate", { price })
              : t("subscribeButton.subscribeTemplate", { price })}
        </button>
      )}
      {sub && sub.state === "cancelled" && stillHasAccess && (
        <span className="text-[10px] text-[var(--color-text3)]">
          {t("subscribeButton.accessUntilTemplate", {
            when: new Date(sub.currentPeriodExpiresAt).toLocaleDateString(),
          })}
        </span>
      )}
      {sub && sub.state === "active" && (
        <span className="text-[10px] text-[var(--color-text3)]">
          {t("subscribeButton.renewsTemplate", {
            when: new Date(sub.currentPeriodExpiresAt).toLocaleDateString(),
          })}
        </span>
      )}
      {sub && sub.state === "failed_renewal_grace" && (
        <span
          className="text-[10px] font-bold"
          style={{ color: "var(--color-no)" }}
        >
          {t("subscribeButton.renewalFailedTemplate", {
            when: new Date(sub.currentPeriodExpiresAt).toLocaleDateString(),
          })}
        </span>
      )}
      {error && (
        <span
          role="alert"
          className="text-[10px]"
          style={{ color: "var(--color-no)" }}
        >
          {error}
        </span>
      )}
    </div>
  );
}
